import {
  TouchEventHandler,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { debounce, isEqual, uniqBy } from 'lodash-es';

import { IStore } from '@rbi-ctg/store';
import { useFavoriteStores } from 'hooks/favorite-stores';
import useEffectOnce from 'hooks/use-effect-once';
import useMap from 'hooks/use-map';
import { usePrevious } from 'hooks/use-previous';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { isValidPosition } from 'utils/geolocation';
import { cornersForPointWithDiagonal } from 'utils/geometry';
import DEFAULT_SEARCH_RADIUS_M from 'utils/restaurant/default-search-radius-meters';
import { routes } from 'utils/routing';

import storeReducer, {
  StoreAction,
  initialState,
  searchNearbyNotGranted,
  setActiveStoreId as setActiveStoreIdAction,
} from '../ducks/stores';
import useStoreLocatorRedirects from '../old-ui/hooks/use-store-locator-redirects';
import { StoreLocatorTabs } from '../types';
import { filterByServiceModeAndSortByDistance } from '../utils';

import useMarkers from './use-markers';
import useSearchRestaurants from './use-search-restaurants';

const setupEvtListeners = (onDragEnd: TouchEventHandler) => ({
  dragend: debounce(onDragEnd, 200),
});

export interface IUseStoreLocator {
  ErrorDialog: React.FunctionComponent;
  Dialog: React.FunctionComponent;
  activeStoreId?: string;
  hasPanned: boolean;
  map: JSX.Element;
  onClickSearch(): void;
  setIsStoreInfoModalOpen(value: boolean): void;
  redirectUrl: string;
  searchingNearby: boolean;
  searchingRecent: boolean;
  searchingFavs: boolean;
  setActiveStoreId: React.Dispatch<any>;
  nearbyStoreCardRefs?: {
    [key: string]: React.MutableRefObject<HTMLDivElement | null>;
  };
  storesNearby?: IStore[];
  storesFavs?: IStore[];
  storesRecent?: IStore[];
  zoom: number;
  currentTab: StoreLocatorTabs;
  handleTabChange(newValue: StoreLocatorTabs): void;
  showGeoAskInNearbyTabWhenEmpty: boolean;
  displayMapViewTabs: boolean;
  enableStoreLocatorDeliveryTab: boolean;
  enableStoreLocatorRecentTab: boolean;
  showStoreLocatorTabs: boolean;
  listViewEnabled: boolean;
  showListView: boolean;
  toggleStoreLocatorView(): void;
  storeLocatorDispatch: StoreAction;
  currentTabHasError: boolean;
}

interface IUseStoreLocatorProps {
  // Loads position and nearest location on mount, default true
  loadPositionOnMount: boolean;
  // Indicates hook use on store locator page, default true
  onStoreLocator: boolean;
}

interface ICurrentcoords {
  lat?: number;
  lng?: number;
}

// TODO: Consider passing context values in, ie: state/payment/usePayment
// can make it easier and more straightforward to test
const defaultArgs = { loadPositionOnMount: true, onStoreLocator: true };
export default function useStoreLocator({
  loadPositionOnMount,
  onStoreLocator,
}: IUseStoreLocatorProps = defaultArgs): IUseStoreLocator {
  const listViewEnabled = useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_LIST_VIEW);
  const [showListView, setShowListView] = useState(listViewEnabled);
  const mParticle = useMParticleContext();

  // When enabled a geolocation ask is shown in the
  // empty state list for nearby on map/list views
  const showGeoAskInNearbyTabWhenEmpty = useFlag(
    LaunchDarklyFlag.ENABLE_GEOLOCATION_ASK_IN_NEARBY_TAB_EMPTY_STORE_LOCATOR
  );

  const storeLocatorRadius =
    useFlag(LaunchDarklyFlag.RADIUS_FOR_STORE_LOCATOR) || DEFAULT_SEARCH_RADIUS_M;

  /** NOTE
   * This hook handles the redirects for /store-locator when
   * - no service mode selected show service mode modal (take-out/delivery). TH skips this modal.
   * - ask for user to share geolocation information if they haven't been asked before
   * - skip service mode [takeout/delivery] ask when query param `?sm=takeout` is present
   */
  // TODO: remove this whole hook once new UI feature rolls out
  //       or figure out a better way to decouple this beforehand
  useStoreLocatorRedirects();

  const [
    {
      storesNearby,
      searchingNearby,
      storesFavs,
      searchingFavs,
      storesRecent,
      searchingRecent,
      activeStoreId,
      errorNearby,
      errorFavs,
      errorRecent,
    },
    storeLocatorDispatch,
  ] = useReducer(storeReducer, initialState);

  const {
    loadCurrentPosition,
    activeCoordinates,
    isPermissionDenied,
    isPermissionPrompt,
  } = useGeolocation();
  const { clearServerOrder, serviceMode } = useOrderContext();
  const [hasPanned, setHasPanned] = useState(false);
  const [isStoreInfoModalOpen, setIsStoreInfoModalOpen] = useState(false);
  const { storeLocatorCallbackUrl } = useLocationContext();
  const [currentcoords, setCurrentCoords] = useState<ICurrentcoords | null>(null);

  const { showFavoritesByDefault, favStoresEnabled } = useFavoriteStores();

  const enableStoreLocatorDeliveryTab = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_DELIVERY_TAB)
  );
  const enableStoreLocatorRecentTab = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_STORE_LOCATOR_RECENT_TAB)
  );

  // Figure out if we can show tabs on the map view
  // - feature flag enabled and at least one tab enabled
  // ADD flags for tabs here as new ones become available
  const showStoreLocatorTabs = [favStoresEnabled, enableStoreLocatorDeliveryTab].some(f => f);
  const displayMapViewTabs = Boolean(useFlag(LaunchDarklyFlag.ENABLE_MAP_VIEW_STORE_TABS));

  // Track current tab for user in list view
  const [currentTab, setTab] = useState<StoreLocatorTabs>(
    showFavoritesByDefault ? StoreLocatorTabs.Favorites : StoreLocatorTabs.Nearby
  );

  // Track whether or not the current tab is in an error state
  const [currentTabHasError, setCurrentTabHasError] = useState(false);

  // update whether or not the current tab has an error
  // check any time the tab itself or any of the tab error states changes
  useEffect(() => {
    let tabHasErr = false;
    switch (currentTab) {
      case StoreLocatorTabs.Nearby:
        if (errorNearby) {
          tabHasErr = true;
        }
        break;
      case StoreLocatorTabs.Favorites:
        if (errorFavs) {
          tabHasErr = true;
        }
        break;
      case StoreLocatorTabs.Recent:
        if (errorRecent) {
          tabHasErr = true;
        }
        break;
      default:
    }
    setCurrentTabHasError(tabHasErr);
  }, [currentTab, errorFavs, errorNearby, errorRecent]);

  const toggleStoreLocatorView = useCallback(() => {
    const selectedMapView = 'Selected map view';
    const selectedListView = 'Selected list view';
    mParticle.logEvent(CustomEventNames.STORE_LOCATOR_TAB, EventTypes.Navigation, {
      'Selected view': showListView ? selectedMapView : selectedListView,
    });

    if (showListView) {
      setTab(StoreLocatorTabs.Nearby);
    }
    setShowListView(s => !s);
  }, [mParticle, showListView]);

  const eventListeners = useMemo(
    () =>
      setupEvtListeners(() => {
        setHasPanned(true);
      }),
    []
  );

  useEffectOnce(() => {
    clearServerOrder();
  });

  const {
    center,
    createMarker,
    domRef,
    map,
    markers,
    neLat,
    neLng,
    panTo,
    swLat,
    swLng,
    zoom,
  } = useMap(
    activeCoordinates
      ? {
          eventListeners,
          position: activeCoordinates,
        }
      : { eventListeners }
  );

  const {
    ErrorDialog,
    Dialog,
    searchNearbyRestaurants,
    searchFavRestaurants,
    searchRecentRestaurants,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });

  /**
   * nearbyStoreCardRefs is used to store the DOM ref for store cards
   *  that also are shown on the map as store icons. When a user
   *  clicks on the store icon on the map the storesNearby associated
   *  nearbyStoreCardRefs ref is scrolled into view
   */
  const nearbyStoreCardRefs = useMemo(
    () =>
      storesNearby?.reduce(
        (o, store) => ({
          ...o,
          [store._id]: createRef(),
        }),
        {}
      ),
    [storesNearby]
  );

  const setActiveStoreId = (storeId: string) => {
    storeLocatorDispatch(setActiveStoreIdAction(storeId));
  };

  const filteredStoresNearby = useMemo(
    () => filterByServiceModeAndSortByDistance(storesNearby, serviceMode, activeCoordinates),
    [storesNearby, serviceMode, activeCoordinates]
  );
  const filteredStoresNearbyPrevValue = usePrevious(filteredStoresNearby);

  const filteredStoresFavs = useMemo(
    () => filterByServiceModeAndSortByDistance(storesFavs, serviceMode, activeCoordinates),
    [storesFavs, serviceMode, activeCoordinates]
  );

  const redirectUrl = storeLocatorCallbackUrl || routes.menu;

  // When map view tabs are enabled we want to combine
  // all the stores for the visible tabs for map markers to work
  const storesWithMarkers = displayMapViewTabs
    ? uniqBy([...filteredStoresNearby, ...filteredStoresFavs], '_id')
    : filteredStoresNearby;

  useMarkers({
    createMarker,
    map,
    markers,
    storesFavs,
    panTo,
    storesNearby: storesWithMarkers,
    activeStoreId,
    storeLocatorDispatch,
    onClickMarker: (store: IStore) => {
      if (store._id === activeStoreId) {
        return;
      }
      mParticle.logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.Other, {
        Name: 'Store card event',
        StoreId: store._id,
      });
      const searchedLocation = {
        lat: store.latitude,
        lng: store.longitude,
      };
      return searchNearbyRestaurants({
        location: searchedLocation,
        bounds: cornersForPointWithDiagonal(
          searchedLocation.lat,
          searchedLocation.lng,
          storeLocatorRadius
        ),
      });
    },
  });

  /*
   * Search restaurants in list view:
   * - only perform search for nearby when we have coords
   */
  const searchRestaurantsForListView = useCallback(
    (coords: ICurrentcoords | null) => {
      // Keep current coordinate state in sync even if we don't trigger a search.
      setCurrentCoords(coords);

      // If the user has not granted geolocalisation permission and has not
      // make a manual search (coords === null), make the nearby store an empty array
      if (!coords && (isPermissionDenied || isPermissionPrompt)) {
        return storeLocatorDispatch(searchNearbyNotGranted());
      }
      // Otherwise, prevent searching nearby stores when the user has not changed it's coords (fix flickering issues)
      if (
        currentcoords?.lat !== coords?.lat &&
        currentcoords?.lng !== coords?.lng &&
        isValidPosition(coords)
      ) {
        return searchNearbyRestaurants({
          location: coords,
          bounds: cornersForPointWithDiagonal(coords.lat, coords.lng, storeLocatorRadius),
        });
      }
    },
    [
      currentcoords,
      isPermissionDenied,
      isPermissionPrompt,
      searchNearbyRestaurants,
      storeLocatorRadius,
    ]
  );

  /*
   * When switching to/from the map we want to switch back to
   * the desired tab based on properties, either nearby or favorites
   *
   * And load that tabs stores if it was the same as previous tab
   */
  const showListViewOldRef = useRef(showListView);
  const currentTabOldRef = useRef(currentTab);
  useEffect(() => {
    if (showListViewOldRef.current === false && showListView) {
      const defaultListViewTab = showFavoritesByDefault
        ? StoreLocatorTabs.Favorites
        : StoreLocatorTabs.Nearby;
      setTab(defaultListViewTab);
    }

    // update refs every call
    showListViewOldRef.current = showListView;
    currentTabOldRef.current = currentTab;
  }, [showFavoritesByDefault, showListView, currentTab]);

  /**
   * Perform map view store searches when:
   * - map is mounted, domRef
   * - user coordinates being updated are valid
   * - user navigates from list view to map view
   * - store info modal is closed
   *
   * NOTE: If not on store locator page e.g onStoreLocator = false, return
   */
  useEffect(() => {
    if (showListView || isStoreInfoModalOpen || !onStoreLocator) {
      return;
    }
    if (domRef.current && isValidPosition(activeCoordinates)) {
      searchNearbyRestaurants({
        location: activeCoordinates,
        bounds: cornersForPointWithDiagonal(
          activeCoordinates.lat,
          activeCoordinates.lng,
          storeLocatorRadius
        ),
      });
    }
  }, [
    activeCoordinates,
    showListView,
    isStoreInfoModalOpen,
    domRef,
    zoom,
    onStoreLocator,
    searchNearbyRestaurants,
    storeLocatorRadius,
  ]);

  useEffect(() => {
    searchFavRestaurants(activeCoordinates);
  }, [activeCoordinates, searchFavRestaurants]);

  useEffect(() => {
    searchRecentRestaurants(activeCoordinates);
  }, [activeCoordinates, searchRecentRestaurants]);

  /*
    Perform list view store searches when:
    - activeCoordinates change
    - showListView changes
    - store info modal is closed
    - Not on the store locator page e.g. onStoreLocator = false;
  */
  useEffect(() => {
    if (!onStoreLocator || (showListView && !isStoreInfoModalOpen)) {
      searchRestaurantsForListView(activeCoordinates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCoordinates, showListView, isStoreInfoModalOpen]);

  /*
   * Try getting user's current position on mount
   */
  useEffectOnce(() => {
    if (loadPositionOnMount) {
      loadCurrentPosition();
    }
  });

  /**
   * Logs "Restaurant Found" mParticle event
   */
  useEffect(() => {
    if (isEqual(filteredStoresNearby, filteredStoresNearbyPrevValue)) {
      return;
    }
    const numberOfRestaurantsFound = filteredStoresNearby.length;
    const numberOfAvailableRestaurants = filteredStoresNearby.filter(r => r.hasMobileOrdering)
      .length;
    const numberOfUnavailableRestaurants = numberOfRestaurantsFound - numberOfAvailableRestaurants;

    mParticle.logEvent(CustomEventNames.RESTAURANTS_FOUND, EventTypes.Other, {
      Restaurants: numberOfRestaurantsFound,
      'Available Restaurants': numberOfAvailableRestaurants,
      'Unavailable Restaurants': numberOfUnavailableRestaurants,
    });
  }, [filteredStoresNearby, filteredStoresNearbyPrevValue, mParticle]);

  const onClickSearch = useCallback(() => {
    searchNearbyRestaurants({
      location: center,
      bounds: {
        minLat: swLat,
        maxLat: neLat,
        minLng: swLng,
        maxLng: neLng,
      },
    });
    setHasPanned(false);
  }, [center, neLat, neLng, searchNearbyRestaurants, swLat, swLng]);

  return {
    ErrorDialog,
    Dialog,
    activeStoreId,
    hasPanned,
    map,
    onClickSearch,
    redirectUrl,
    searchingNearby: searchingNearby || !storesNearby,
    searchingRecent: searchingRecent || !storesRecent,
    searchingFavs: searchingFavs || !storesFavs,
    setActiveStoreId,
    nearbyStoreCardRefs,
    storesNearby: filteredStoresNearby,
    storesFavs: filteredStoresFavs,
    storesRecent,
    zoom,
    displayMapViewTabs,
    enableStoreLocatorDeliveryTab,
    enableStoreLocatorRecentTab,
    showStoreLocatorTabs,
    showGeoAskInNearbyTabWhenEmpty,
    listViewEnabled,
    showListView,
    toggleStoreLocatorView,
    currentTab,
    handleTabChange: setTab,
    setIsStoreInfoModalOpen,
    storeLocatorDispatch,
    currentTabHasError,
  };
}
